import { Box } from '@mui/material'
import { usePlanningStore } from '@planning/AppProvider'
import { InitializationWrapper } from '@planning/components'
import { useTranslate } from '@tolgee/react'
import { Breadcrumbs, Loader, PageHeader, useMinimalsTheme } from '@tom-ui/ui'
import { PATHS, useAsyncFetch } from '@tom-ui/utils'
import { observer } from 'mobx-react-lite'
import { FC, useMemo } from 'react'
import { Helmet } from 'react-helmet-async'
import { useNavigate, useParams } from 'react-router'
import { NumericReleaseForm } from '../../Components/organisms/NumericReleases/NumericReleaseForm'
import {
  NumericReleaseControlViewStore,
  NumericReleaseFormData,
} from '../../Stores/NumericReleaseControlViewStore'
import { useReleaseMessagesHandler } from '../../hooks/useReleaseMessagesHandler'

export const NumericReleaseFormControlPage: FC = observer(() => {
  const { t } = useTranslate()
  const { appViewStore, numericReleaseItemStore } = usePlanningStore()
  const navigate = useNavigate()
  const theme = useMinimalsTheme()
  const { getNumericReleaseSuccessMessage, getExceptionMessage } = useReleaseMessagesHandler()

  const numericReleaseControlViewStore = useMemo(
    () => new NumericReleaseControlViewStore(numericReleaseItemStore),
    [numericReleaseItemStore],
  )

  const editLabel = t('editNumericRelease', 'Edit numeric release')
  const createLabel = t('createNumericRelease', 'Create numeric release')

  const { numericReleaseId } = useParams()

  const title = numericReleaseId ? editLabel : createLabel

  const operator = numericReleaseControlViewStore.selectedNumericRelease?.operator?.data.shortName
  const referenceNumber =
    numericReleaseControlViewStore.selectedNumericRelease?.data.referenceNumber

  const identifier = operator ? `${operator}-${referenceNumber}` : referenceNumber

  const breadcrumbs = [
    {
      label: t('numericReleases', 'Numeric releases'),
      onClick: () => navigate(`${PATHS.orders.numericReleases.url}`),
    },
    {
      label: numericReleaseId
        ? `${t('editNrBreadcrumb', 'Edit {identifier}', { identifier })}`
        : createLabel,
    },
  ]

  const { loading } = useAsyncFetch(async () => {
    const id = Number(numericReleaseId)

    if (id > 0) {
      numericReleaseControlViewStore.setId(id)
      await numericReleaseItemStore.fetchById(id)
    }
  }, [numericReleaseId, numericReleaseControlViewStore, numericReleaseItemStore])

  const onFormSubmit = async (data: NumericReleaseFormData) => {
    try {
      numericReleaseControlViewStore.setIsLoading(true)
      await numericReleaseControlViewStore.onSubmit(data)
      appViewStore.setShowAlert(
        'success',
        getNumericReleaseSuccessMessage(numericReleaseControlViewStore.isEditMode),
      )
      navigate(`${PATHS.orders.numericReleases.url}`)
    } catch (error) {
      appViewStore.setShowAlert('error', getExceptionMessage(error))
    } finally {
      numericReleaseControlViewStore.setIsLoading(false)
    }
  }

  return (
    <InitializationWrapper isInitializing={loading}>
      <>
        <Loader show={numericReleaseControlViewStore.isLoading} />
        <Helmet>
          <title>{title}</title>
        </Helmet>
        <Breadcrumbs items={breadcrumbs} />
        <PageHeader
          pageTitle={title}
          formId='numeric-release-form'
          onBack={() => navigate(`${PATHS.orders.numericReleases.url}`)}
        />
        <Box
          paddingX={theme.customSpacing.l}
          paddingY={theme.customSpacing.m}
          gap={theme.customSpacing.m}
          display={'flex'}
          flexDirection={'column'}
        >
          <NumericReleaseForm viewStore={numericReleaseControlViewStore} onSubmit={onFormSubmit} />
        </Box>
      </>
    </InitializationWrapper>
  )
})
